import React from 'react';
import { Badge } from '@tadagram/ui';
import { SeedingAccount } from '../types';

/**
 * Derives the display badge for a seeding account.
 *
 * Account status lifecycle (persisted in MongoDB seeding_accounts.status):
 *   pending   → just added, worker is logging in           [warning]
 *   active    → worker login succeeded                     [success]
 *   failed    → worker login failed (wrong pw, 2FA, etc.)  [danger]
 *   locked    → platform locked the account               [danger]
 *   checkpoint→ platform security check required          [warning]
 *   disabled  → manually disabled by user                 [neutral]
 *   banned    → account permanently banned                [danger]
 *
 * Worker runtime state (from Redis via workerStatusMap) is overlaid ONLY
 * for active accounts — a pending/failed account doesn't care if worker is offline.
 */
export function getAccountBadge(
  account: Pick<SeedingAccount, 'status' | 'worker_uuid'>,
  workerStatusMap: Record<string, string> = {},
): React.ReactElement {
  // ── 1. Persistent business/login states — always take priority ───────────
  switch (account.status) {
    case 'pending':
      return <Badge variant="warning">Pending</Badge>;
    case 'failed':
      return <Badge variant="danger">Login failed</Badge>;
    case 'locked':
      return <Badge variant="danger">Locked</Badge>;
    case 'checkpoint':
      return <Badge variant="warning">Checkpoint</Badge>;
    case 'disabled':
      return <Badge variant="neutral">Disabled</Badge>;
    case 'banned':
      return <Badge variant="danger">Banned</Badge>;
  }

  // ── 2. active: overlay with worker runtime state from Redis ──────────────
  const ws = account.worker_uuid ? workerStatusMap[account.worker_uuid] : undefined;
  if (ws === 'expired') return <Badge variant="danger">Worker expired</Badge>;
  if (ws === 'offline') return <Badge variant="warning">Worker offline</Badge>;
  // 'idle' = scheduled worker between runs (normal), 'online' = running, undefined = no worker
  return <Badge variant="success">Active</Badge>;
}
